'use client';
import { useEffect } from 'react';
import { Box, Typography, Container, Paper } from '@mui/material';
import Header from '../components/HomepageContent/Header';
import Footer from '../components/HomepageContent/Footer';
import Link from 'next/link';

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'CareAscendium - Privacy Policy';
  }, []);
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 50%, #2A5A9C 100%)',
        py: 8, 
        px: 4,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h1" 
            sx={{ 
              color: 'white', 
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            Privacy Policy
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            Your privacy is important to us
          </Typography>
        </Container>
      </Box>

      {/* Privacy Policy Content */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 4, md: 6 },
            bgcolor: 'white',
            borderRadius: 3,
            border: '1px solid rgba(21,60,127,0.1)'
          }}
        >
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              CareAscendium, LLC. ("CareAscendium," "we," "us," or "our"), an Indiana limited liability company, is sensitive to your concerns about the use of the information you disclose to us via our web and/or mobile application (collectively, the "Application"). This Privacy Policy (the "Policy") sets forth our privacy practices and the possible uses of the information we gather through the Application. We will not sell, share, or rent your personal information in ways different from those disclosed in this Policy.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              This Policy applies to information we collect through the Application and does not apply to information collected offline or through other means. By using the Application, you consent to our collection and use of your information as described in this Policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Collection of Information
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              In connection with your use of the Application, we may request and/or collect certain personal information from you, which may include, without limitation, your name, address, email address, employer, job title, or other identifiers ("Personal Information"). We may also collect non-personal information, such as device type, IP address, browser type, and usage data.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              By entering your Personal Information via the Application or otherwise providing it to us, you consent to our use of your Personal Information for the purposes discussed herein. Although not expected, to the extent we collect or store any protected health information ("PHI") as defined under the Health Insurance Portability and Accountability Act of 1996 ("HIPAA") and its implementing regulations (45 CFR Parts 160-164), we will comply with all applicable HIPAA requirements regarding your PHI, including through our Business Associate Agreement (BAA) with covered entities.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We may automatically collect information using common tools like cookies, web beacons, and server logs (see "Aggregation and Cookies" below).
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Use of Information
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We will use your Personal Information to administer and support the Application, provide you access to its functions, and interact with your employer or other primary licensor pursuant to which you have access to the Application. We will also use it to contact you with information regarding new features, updates, and other issues relating to the Application, or to respond to your requests, including technical support. We may refer to your Personal Information to better understand your needs and improve the Application.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We may use non-personal or aggregated information for internal purposes, such as analytics, product improvement, or AI training, in a de-identified manner compliant with applicable laws (e.g., HIPAA de-identification standards).
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Information Sharing
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We may share your Personal Information with agents, contractors, or partners in connection with services they perform for us, such as hosting the Application, data processing, or sending materials to users. We require these third parties to use your Personal Information only for these purposes and in accordance with this Policy, and we prohibit them from advertising to you or contacting you without our authorization.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We reserve the right to share your Personal Information in response to duly authorized information requests of governmental authorities or where required by law. We may also disclose it if we deem it necessary or desirable in our sole discretion (i) for fraud protection or credit risk reduction purposes, (ii) in the good-faith belief that such action is necessary to protect and defend our rights or property, our licensors, or the rights, property, or wellbeing of any users of the Application or other third party, or (iii) to act under urgent circumstances to protect our safety or that of our employees or the public.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              If we collect PHI, sharing is governed by HIPAA and our BAA.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Aggregation and Cookies
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              Any of your Personal Information that is aggregated in a non-personally identifiable manner may be disclosed to third parties and otherwise used by us as we deem appropriate. In addition, we may collect information through common tools such as web beacons, cookies, and server logging. For example, the Application may use a technology called a "cookie." A cookie is a piece of information that the web server sends to your device when you access the Application. When you return to the Application, it will detect whether you have one of the cookies on your device. The cookies help provide additional functionality to the Application and help us analyze Application usage more accurately. For instance, the Application may set a cookie that keeps you from needing to re-enter your username or password. This Policy covers the use of cookies by us and does not cover the use of cookies by any advertisers or other third parties.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              You can manage cookie preferences through your browser settings or our Application settings. For more on cookies, see our{' '}
              <Link 
                href="/cookie-policy" 
                style={{ 
                  color: '#153C7F',
                  textDecoration: 'none',
                  fontWeight: 600
                }}
              >
                Cookie Policy
              </Link>
              .
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Push Notifications
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              You acknowledge that we may send push notifications to you through the Application and/or to your email. You can opt out of push notifications at any time through your device or Application settings.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Comments
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              Comments and other content submitted to our anti-spam service are not saved on our servers unless they were marked as false positives, in which case we store them long enough to use them to improve the service to avoid future false positives.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Business Transfer
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We may provide your Personal Information to a third party in connection with the sale, assignment, or other transfer of our business to which the information relates, in which case we will require any such buyer to agree to treat your Personal Information in accordance with this Policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Data Security
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We implement reasonable physical, administrative, and technical safeguards to protect your Personal Information, including encryption, access controls, and regular security audits. However, no method of transmission over the Internet or electronic storage is 100% secure, so we cannot guarantee absolute security.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Your Rights
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              Depending on your location (e.g., California under CCPA/CPRA, or other state laws), you may have rights to access, correct, delete, or opt out of the sale/sharing of your Personal Information. To exercise these rights, contact us at the address below. We will respond within the timeframe required by law (e.g., 45 days for CCPA requests).
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We do not knowingly collect Personal Information from children under 13. If we learn we have collected such information, we will delete it.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              For international users, data may be transferred to the U.S.; we comply with applicable laws for cross-border transfers.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Privacy Policy Changes
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              Although most changes are likely to be minor, we may change our Privacy Policy from time to time. We will notify you of material changes via email or in-App notice at least 30 days before they take effect. We encourage you to frequently check this page for any changes. Your continued use of the Application after any change constitutes your acceptance of such change.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Privacy Policy Contact Information
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              If you have any questions, concerns, or complaints regarding this Policy or our collection or use of your Personal Information, please contact us at your convenience:
            </Typography>
            <Box sx={{ mt: 3, pl: 2, borderLeft: '3px solid #153C7F' }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  lineHeight: 2,
                  color: '#333',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                CareAscendium, LLC.
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  lineHeight: 2,
                  color: '#333',
                  fontSize: '1rem'
                }}
              >
                Attn: Privacy and Security
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  lineHeight: 2,
                  color: '#333',
                  fontSize: '1rem'
                }}
              >
                12486 Shadow Cove Way
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  lineHeight: 2,
                  color: '#333',
                  fontSize: '1rem'
                }}
              >
                Carmel, IN 46033
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  lineHeight: 2,
                  color: '#333',
                  fontSize: '1rem'
                }}
              >
                <Link 
                  href="mailto:info@CareAscendium.com" 
                  style={{ 
                    color: '#153C7F',
                    textDecoration: 'none',
                    fontWeight: 600
                  }}
                >
                  info@CareAscendium.com
                </Link>
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(21,60,127,0.1)' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666',
                fontSize: '0.9rem',
                fontStyle: 'italic'
              }}
            >
              Last Updated: October 30, 2025
            </Typography>
          </Box>
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
}

